import { IAcquirerOnboarding } from '@/@types/AcquirerOnboarding';
import Checked from '@/assets/icons/Checked';
import Unchecked from '@/assets/icons/Unchecked';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useAcquirerOnboarding } from '../hooks/useAcquirerOnboarding';
import { AcquirerFieldsForm } from './AcquirerFieldsForm';

/**
 * Passo de verificação da adquirente — o KYC que acontece num formulário do provedor.
 *
 * Genérico: uma linha por adquirente que a API listar. Nenhum nome de provedor aparece no código
 * nem na tela; a marca do provedor só existe dentro do próprio formulário.
 */
export function AcquirerStep() {
  const {
    onboardings,
    isPending,
    isError,
    errorMessage,
    openHostedForm,
    openingFor,
    submitFields,
    isSubmitting,
  } = useAcquirerOnboarding();

  if (isPending) {
    return null;
  }

  // Falha e ausência não podem virar a mesma tela vazia: sem isto, um erro na consulta some sem
  // deixar rastro e o vendedor fica sem entender por que o passo não aparece.
  if (isError) {
    return (
      <li className='flex items-center gap-4 py-2'>
        <AlertCircle className='h-5 w-5 text-red-500 shrink-0' />
        <p className='text-sm text-muted-foreground'>
          Não foi possível carregar suas contas de recebimento.
          {errorMessage ? ` (${errorMessage})` : ''}
        </p>
      </li>
    );
  }

  if (onboardings.length === 0) {
    return null;
  }

  return (
    <>
      {onboardings.map((onboarding) => (
        <AcquirerOnboardingRow
          key={onboarding.acquirer}
          onboarding={onboarding}
          isOpening={openingFor === onboarding.acquirer}
          onOpen={() => openHostedForm(onboarding)}
          isSubmitting={isSubmitting}
          onSubmitFields={(values) =>
            submitFields({ acquirer: onboarding.acquirer, values })
          }
        />
      ))}
    </>
  );
}

interface AcquirerOnboardingRowProps {
  onboarding: IAcquirerOnboarding;
  isOpening: boolean;
  onOpen: () => void;
  isSubmitting: boolean;
  onSubmitFields: (values: Record<string, unknown>) => Promise<unknown>;
}

function AcquirerOnboardingRow({
  onboarding,
  isOpening,
  onOpen,
  isSubmitting,
  onSubmitFields,
}: AcquirerOnboardingRowProps) {
  const [showFields, setShowFields] = useState(false);
  const isApproved = onboarding.status === 'approved';
  const isRejected = onboarding.status === 'rejected';

  return (
    <li className='flex flex-col gap-2 py-2'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4 h-14'>
          {isApproved ? (
            <Checked />
          ) : (
            <Unchecked className={isRejected ? 'text-red-500' : undefined} />
          )}
          {/* Nunca exibir o nome da adquirente ao vendedor. Sem badge: o ícone já diz o
              estado, igual aos demais passos da tela. */}
          <p className='font-medium'>Conta de recebimento</p>
        </div>

        {(onboarding.needs_fields ||
          onboarding.needs_merchant_action ||
          onboarding.awaiting_provider) && (
          <Button
            onClick={() =>
              onboarding.needs_fields || onboarding.awaiting_provider
                ? setShowFields(true)
                : onOpen()
            }
            disabled={isOpening}
            variant='outline'
            size='icon'
            className='h-9 w-12'
            aria-label={
              onboarding.needs_fields || onboarding.awaiting_provider
                ? 'Informar dados da conta de recebimento'
                : 'Abrir verificação'
            }
          >
            {isOpening ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <ArrowRight />
            )}
          </Button>
        )}
      </div>

      {onboarding.needs_fields &&
        onboarding.requires_product &&
        !onboarding.has_product && (
          <p className='text-sm text-red-500 pl-14'>
            Crie um produto antes de enviar os dados da conta de recebimento.
          </p>
        )}

      {/* Em análise não tem botão nem ação possível: sem esta linha o vendedor veria um item
          pendente e nada explicando por quê. */}
      {onboarding.status === 'under_review' && (
        <p className='text-sm text-muted-foreground pl-14'>
          Sua verificação está em análise. Avisaremos assim que houver retorno.
        </p>
      )}

      {/* Reprovação bloqueia só esta adquirente — o vendedor segue vendendo pelas outras. */}
      {isRejected && onboarding.rejection_reason && (
        <p className='text-sm text-muted-foreground pl-14'>
          {onboarding.rejection_reason}
        </p>
      )}

      {/* Dados enviados, conta ainda não criada no provedor. Falha nossa não é problema do
          vendedor — ele não é cobrado, não vê erro técnico, e esta adquirente é só mais uma.
          Quem reprocessa é o admin. */}
      {onboarding.awaiting_provider && (
        <p className='text-sm text-muted-foreground pl-14'>
          Recebemos seus dados. Assim que a verificação for liberada, o botão
          para continuar aparece aqui.
        </p>
      )}

      {/* Faltam dados exigidos no payload de criação: o formulário vem antes do KYC, e abre em
          sheet como os demais passos do onboarding. */}
      {(onboarding.needs_fields || onboarding.awaiting_provider) && (
        <AcquirerFieldsForm
          onboarding={onboarding}
          isSubmitting={isSubmitting}
          open={showFields}
          onClose={() => setShowFields(false)}
          onSubmit={async (values) => {
            // Só fecha DEPOIS de processar: fechar antes tirava o feedback da tela e, ao
            // reabrir, o vendedor via o formulário ainda carregando sem saber o que aconteceu.
            try {
              await onSubmitFields(values);
              setShowFields(false);
            } catch {
              // Falha mantém o sheet aberto para correção; o toast de erro vem do hook.
            }
          }}
        />
      )}

      {onboarding.needs_merchant_action &&
        !onboarding.needs_fields &&
        onboarding.required_documents.length > 0 && (
          <div className='pl-14'>
            <p className='text-sm text-muted-foreground mb-1'>
              Tenha em mãos antes de começar:
            </p>
            <ul className='text-sm text-muted-foreground list-disc pl-4 space-y-0.5'>
              {onboarding.required_documents.map((document) => (
                <li key={document}>{document}</li>
              ))}
            </ul>
          </div>
        )}
    </li>
  );
}
